/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Scale } from '../../UNSAFE_Chart';
import { getAxisBoundsByDelta } from './utils';
type ViewportHandlerProps = {
  width: number;
  height: number;
  plotAreaSpace: { x: number; y: number; width: number; height: number };
  isRtl: boolean;
  isHoriz: boolean;
  xViewportExtent: { viewportMin: number; viewportMax: number };
  yViewportExtent: { viewportMin: number; viewportMax: number };
  xAxisExtent: { min: number; max: number };
  yAxisExtent: { min: number; max: number };
  yScale: Scale;
  xScale: Scale;
  zoomAndScroll: 'live' | 'off';
  onViewportChange?: (detail: any) => void;
};

const VIEWPORT_SCALE = 0.05;

export function useChartViewport({
  plotAreaSpace,
  xViewportExtent,
  xAxisExtent,
  yAxisExtent,
  yViewportExtent,
  yScale,
  xScale,
  onViewportChange,
  isHoriz,
  zoomAndScroll,
  isRtl
}: ViewportHandlerProps) {
  const onWheel = (event: WheelEvent) => {
    const delta = (isHoriz ? 1 : -1) * event.deltaY * VIEWPORT_SCALE;
    const deltaXMin = delta * (event.offsetX - plotAreaSpace.x);
    const deltaXMax = -delta * (plotAreaSpace.x + plotAreaSpace.width - event.offsetX);
    const deltaYMin = delta * (event.offsetY - plotAreaSpace.y);
    const deltaYMax = -delta * (plotAreaSpace.y + plotAreaSpace.height - event.offsetY);
    const extents = getAxisBoundsByDelta(
      xViewportExtent,
      xAxisExtent,
      yViewportExtent,
      yAxisExtent,
      xScale,
      yScale,
      deltaXMin,
      deltaXMax,
      deltaYMin,
      deltaYMax,
      isRtl,
      isHoriz
    );
    onViewportChange?.({
      xMin: extents.xExtents.viewportMin,
      yMin: extents.yExtents.viewportMin,
      xMax: extents.xExtents.viewportMax,
      yMax: extents.yExtents.viewportMax
    });
    event.preventDefault();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    const zoomIn = key === '+' || key === '=';
    const zoomOut = key === '-' || key === '_';

    if (!zoomIn && !zoomOut) {
      return;
    }
    const zoomRatio = zoomIn ? 1.5 : 1 / 1.5;
    const shiftRatio = (1 / zoomRatio - 1) / 2;

    const deltaXMin = -1 * shiftRatio * plotAreaSpace.width;
    const deltaXMax = shiftRatio * plotAreaSpace.width;
    const deltaYMin = -1 * shiftRatio * plotAreaSpace.height;
    const deltaYMax = shiftRatio * plotAreaSpace.height;

    const extents = getAxisBoundsByDelta(
      xViewportExtent,
      xAxisExtent,
      yViewportExtent,
      yAxisExtent,
      xScale,
      yScale,
      deltaXMin,
      deltaXMax,
      deltaYMin,
      deltaYMax,
      isRtl,
      isHoriz
    );
    onViewportChange?.({
      xMin: extents.xExtents.viewportMin,
      yMin: extents.yExtents.viewportMin,
      xMax: extents.xExtents.viewportMax,
      yMax: extents.yExtents.viewportMax
    });
  };

  return onViewportChange && zoomAndScroll === 'live' ? { onWheel, onKeyDown } : {};
}
